import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Sparkles, ChevronLeft, AlertCircle } from "lucide-react";
import { getTopupPacks } from "../../api/wallet.api";
import { checkoutTopup, getCheckoutStatus } from "../../api/payments.api";
import { loadCashfree, openCashfreeCheckout } from "../../lib/cashfree";
import useWalletStore from "../../store/wallet.store";
import { emit } from "../../lib/walletEvents";
import WhatYouCanDo from "../../components/wallet/WhatYouCanDo";
import useBillingGate from "../../components/billing/useBillingGate";
import { priceBreakdown } from "../../components/billing/formatGstPrice";

export default function Topup() {
  const balance = useWalletStore((s) => s.balance);
  const fetchBalance = useWalletStore((s) => s.fetchBalance);
  const { ensureProfile: ensureBilling, Modal: BillingModal } = useBillingGate();

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPack, setBusyPack] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchBalance();
    getTopupPacks()
      .then((res) => setPacks(res.data || []))
      .catch(() => setError("Could not load topup packs. Please try again."))
      .finally(() => setLoading(false));
  }, [fetchBalance]);

  // Poll the wallet for the credited balance after Drop-in closes.
  // Two paths credit the wallet:
  //   1. Cashfree webhook → backend handler → instant credit (usually < 5s)
  //   2. Reconciliation cron (every 2 min) → fallback when webhook fails
  // Poll budget = ~150s covers both paths. Progressive copy keeps the
  // operator informed that the system is actively verifying, not hung.
  const pollUntilCredited = (startBalance, expectedCredits) => {
    let tries = 0;
    const MAX_TRIES = 100;       // 100 × 1500ms = 150s
    const poll = setInterval(async () => {
      tries++;
      const newBal = await fetchBalance();
      // 2026-06-17 F8 — detect mid-poll session expiry. fetchBalance
      // swallows 401/403 and returns null; previously the loop kept
      // running until MAX_TRIES and then claimed "haven't landed" even
      // though the user was logged out. Surface the auth issue
      // immediately so the user can re-login and verify.
      if (newBal === null && tries > 2) {
        clearInterval(poll);
        setError(
          "Your session expired while verifying the payment. Sign in " +
          "again — if Cashfree captured the payment, credits will appear " +
          "in your wallet within a couple of minutes."
        );
        return;
      }
      if (newBal && newBal.balance_credits > startBalance) {
        clearInterval(poll);
        setSuccess(
          `✓ Done — ${newBal.balance_credits - startBalance} Zencredits added to your wallet.`
        );
        emit("wallet_changed");
        return;
      }
      // Progressive milestone copy. Times are tries × 1.5s.
      if (tries === 4) {                        // 6s
        setSuccess("Payment confirmed. Checking wallet…");
      } else if (tries === 20) {                // 30s
        setSuccess("Still verifying — most credits land within 30-60 seconds…");
      } else if (tries === 60) {                // 90s
        setSuccess("Taking a little longer than usual — running our reconciliation check (up to 2 min)…");
      } else if (tries >= MAX_TRIES) {
        clearInterval(poll);
        setSuccess(
          `Payment received but ${expectedCredits} Zencredits haven't landed yet. ` +
          "They usually arrive within 2 minutes. Refresh this page — they may have just landed. " +
          "If they don't show after refresh, email admin with your order ID and we'll reconcile manually."
        );
      }
    }, 1500);
  };

  const handleBuy = async (pack) => {
    setError(""); setSuccess(""); setBusyPack(pack.code);

    // 2026-06-20 — collect billing details before redirecting to
    // Cashfree. We need state for the GST invoice + an address if the
    // customer wants ITC. Cached per-session so repeat buyers don't
    // see the modal twice.
    const proceed = await ensureBilling({ purpose: "topup" });
    if (!proceed) {
      setBusyPack(null);
      return;
    }

    // 1. Ask the backend to mint a Cashfree order; we get back a
    //    payment_session_id the Drop-in needs.
    let orderRes;
    try {
      orderRes = await checkoutTopup(pack.code, window.location.href);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not start payment. Try again.";
      setError(detail);
      setBusyPack(null);
      return;
    }
    const { payment_session_id, credits, order_id } = orderRes.data;
    if (!payment_session_id) {
      setError("Backend didn't return a payment session. Please try again.");
      setBusyPack(null);
      return;
    }

    // 2. Lazy-load the Cashfree SDK + open Drop-in. We use REDIRECT
    //    mode (full-page navigate to Cashfree's hosted checkout) because
    //    Cashfree's production merchant accounts require origin
    //    whitelisting before the modal iframe will render — and that
    //    process takes 1–2 business days to approve. Redirect mode
    //    works on day-zero production with no whitelist needed.
    //    Switch back to "modal" once Cashfree confirms zencricketdata.com
    //    is in Allowed Origins.
    let cashfree;
    try {
      cashfree = await loadCashfree();
    } catch (e) {
      setError("Cashfree Drop-in failed to load. Check your network.");
      setBusyPack(null);
      return;
    }

    // Stash order context so the post-redirect page load can verify it.
    // Redirect mode navigates the browser fully — JS state is lost on
    // return, so we use sessionStorage as a tiny handoff buffer.
    try {
      sessionStorage.setItem("zcd_pending_topup", JSON.stringify({
        order_id, credits, startBalance: balance?.balance_credits ?? 0,
        ts: Date.now(),
      }));
    } catch { /* private mode — non-fatal, verification falls back to URL params */ }

    try {
      // Redirect mode: SDK schedules a window.location navigation, then
      // resolves the promise immediately with an empty result. We must
      // NOT treat that resolution as success — the user hasn't paid yet.
      // Show a transient "Redirecting…" hint; the real verdict comes on
      // page reload via verifyReturnedOrder() below.
      setSuccess("Redirecting to Cashfree…");
      await openCashfreeCheckout(cashfree, {
        paymentSessionId: payment_session_id,
        returnUrl: window.location.origin + window.location.pathname,
        mode: "redirect",
      });
      // If we're still on this page 5 seconds after calling checkout, the
      // navigation didn't fire (CSP block, popup blocker, network). Warn.
      //
      // 2026-06-17 F2 — the original 3-second timer fired false positives
      // on slow networks because Cashfree's hosted page can take 1-4s to
      // start streaming. Now: extend to 5s AND cancel the timer on
      // `pagehide` / `beforeunload` (which fires the moment the browser
      // starts navigating away). If the navigation kicked off, the timer
      // is dead before it could show the false error.
      const navigationTimer = setTimeout(() => {
        if (document.visibilityState === "visible") {
          setBusyPack(null);
          setSuccess("");
          setError("Couldn't open Cashfree checkout. If this keeps happening, refresh and retry, or contact support.");
        }
      }, 5000);
      const cancelNavTimer = () => {
        clearTimeout(navigationTimer);
        window.removeEventListener("pagehide", cancelNavTimer);
        window.removeEventListener("beforeunload", cancelNavTimer);
      };
      window.addEventListener("pagehide", cancelNavTimer);
      window.addEventListener("beforeunload", cancelNavTimer);
    } catch (e) {
      setBusyPack(null);
      setSuccess("");
      setError(e?.message || "Cashfree checkout failed to open. Please try again.");
    }
  };

  // Post-return verification. When Cashfree navigates back to returnUrl,
  // it appends ?order_id=... (sometimes ?cf_order_id=). We read the
  // pending order from sessionStorage, ask the backend for its current
  // status, and start the credit poll only if Cashfree confirms PAID.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnedOrderId = params.get("order_id") || params.get("cf_order_id");
    let pending = null;
    try {
      const raw = sessionStorage.getItem("zcd_pending_topup");
      if (raw) pending = JSON.parse(raw);
    } catch { pending = null; }
    if (!returnedOrderId && !pending) return;
    // Clear pending immediately so a manual refresh doesn't re-verify.
    try { sessionStorage.removeItem("zcd_pending_topup"); } catch {}
    // Strip the query so the URL stays clean.
    if (returnedOrderId) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    const orderId = returnedOrderId || pending?.order_id;
    if (!orderId) return;
    (async () => {
      try {
        const res = await getCheckoutStatus(orderId);
        const status = (res?.data?.status || res?.data?.order_status || "").toUpperCase();
        if (status === "PAID" || status === "ACTIVE" || status === "SUCCESS") {
          setSuccess(`Payment received (order ${orderId}). Verifying with bank…`);
          pollUntilCredited(pending?.startBalance ?? 0, pending?.credits ?? 0);
        } else if (status === "EXPIRED" || status === "CANCELLED" || status === "TERMINATED") {
          setError(`Payment was ${status.toLowerCase()}. No money was charged.`);
        } else {
          // Unknown / pending — let the user know but don't claim success.
          setSuccess("Payment status pending. Refresh in a minute; if not credited, contact support.");
        }
      } catch (e) {
        setError("Could not verify order status. If you were charged, contact support with the order ID.");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Link
          to="/account/wallet"
          className="text-text-secondary hover:text-text-primary flex items-center gap-1 text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Wallet
        </Link>
      </div>
      <h1 className="text-2xl font-semibold text-text-primary">Buy Zencredits</h1>
      <p className="text-sm text-text-secondary mt-1">
        Zencredits power AI commentary, ball tagging, match-report PDFs, and chat assistant. 1 Zencredit = ₹1.
      </p>

      {balance && (
        <div className="mt-4 mb-6 inline-flex items-center gap-2 bg-bg-primary border border-border-default rounded-md px-3 py-2 text-sm">
          <Coins className="w-4 h-4 text-emerald-400" />
          <span className="text-text-secondary">Current balance:</span>
          <span className="text-text-primary font-semibold tabular-nums">{balance.balance_credits} {balance.balance_credits === 1 ? "Zencredit" : "Zencredits"}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-rose-500/10 border border-rose-500/40 rounded-md p-3 text-sm text-rose-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-emerald-500/10 border border-emerald-500/40 rounded-md p-3 text-sm text-emerald-200">
          {success}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Loading packs…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {packs.map((pack) => {
            const isBusy = busyPack === pack.code;
            const bonus = pack.bonus_credits;
            const isBest = bonus / pack.granted_credits > 0.20;
            return (
              <div
                key={pack.code}
                className={`bg-bg-primary border rounded-xl p-5 flex flex-col relative ${
                  isBest ? "border-button-primary shadow-lg shadow-button-primary/20" : "border-border-default"
                }`}
              >
                {isBest && (
                  <span className="absolute -top-2 right-4 bg-button-primary text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                    Best value
                  </span>
                )}
                <h3 className="font-semibold text-text-primary text-lg">{pack.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-text-primary">₹{pack.price_inr}</span>
                </div>
                {/* 2026-06-20 — GST line. Tax-inclusive packs (the
                    grandfathered default) show "incl. 18% GST" quietly;
                    tax-exclusive packs spell out the +18% + total. */}
                {(() => {
                  const pb = priceBreakdown(pack);
                  return (
                    <div className="mt-0.5 text-[11px] text-text-secondary leading-tight">
                      <div>{pb.taxLine}</div>
                      {pb.totalLabel && (
                        <div className="text-text-primary font-medium">
                          {pb.totalLabel}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Coins className="w-4 h-4 text-emerald-400" />
                  <span className="text-text-primary font-medium tabular-nums">{pack.granted_credits} Zencredits</span>
                  {bonus > 0 && (
                    <span className="text-xs bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">+{bonus} bonus</span>
                  )}
                </div>
                {pack.description && (
                  <p className="mt-3 text-xs text-text-secondary flex-1">{pack.description}</p>
                )}
                <button
                  onClick={() => handleBuy(pack)}
                  disabled={!!busyPack}
                  className={`mt-5 w-full py-2 rounded-md text-sm font-semibold transition-opacity ${
                    isBest
                      ? "bg-button-primary text-white hover:opacity-90"
                      : "bg-bg-secondary text-text-primary hover:bg-bg-secondary/70"
                  } disabled:opacity-50`}
                >
                  {isBusy ? (
                    <span className="flex items-center justify-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                      Loading…
                    </span>
                  ) : (
                    `Buy for ₹${
                      pack.is_tax_inclusive === false && pack.charge_paise
                        ? (pack.charge_paise / 100).toLocaleString("en-IN", {
                            minimumFractionDigits: 2, maximumFractionDigits: 2,
                          })
                        : pack.price_inr
                    }`
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-8 text-xs text-text-secondary">
        Secure payment by Cashfree. Zencredits are added to your wallet within seconds of a successful payment.
      </p>

      <div className="mt-8">
        <WhatYouCanDo />
      </div>

      <BillingModal />
    </div>
  );
}
